import {
    AvailabilityStatus,
    Brand,
    Benefit,
    ImageSearchRequest,
    Listing,
    ListingFreshness,
    ListingOption,
    OptionAvailability,
    Platform,
    PriceAlert,
    PriceSnapshot,
    ProductAttributeKind,
    ProductAttributeProfile,
    ProductAttributeValue,
    ProductCategory,
    ProductCategoryPath,
    ProductCategoryRoot,
    ProductCluster,
    ProductListing,
    ProductSubcategory,
    Seller,
    SourceStatus,
    UserSavedItem,
    Variant,
    WholesaleTag
} from './domain';
import {
    calculatePublicEffectivePrice,
    normalizeListingFreshness,
    validateListingEffectivePrices,
    validatePriceSnapshotEffectivePrices
} from './price-utils';
import { lookitDefaultCategoryRegions } from './category-default-regions';

const syncTime = '2026-06-26T09:00:00+09:00';

const imageBase = 'https://images.unsplash.com';
const topDefaultRegion = lookitDefaultCategoryRegions.top;
const bottomDefaultRegion = lookitDefaultCategoryRegions.bottom;
const shoesDefaultRegion = lookitDefaultCategoryRegions.shoes;

export const lookitCategories: ProductCategory[] = [
    { id: 'top', label: '상의', level: 'root', parent_id: null, sort_order: 10, attribute_schema: ['color', 'material', 'pattern', 'fit', 'season', 'gender', 'style'] },
    { id: 'bottom', label: '하의', level: 'root', parent_id: null, sort_order: 20, attribute_schema: ['color', 'material', 'pattern', 'fit', 'season', 'gender', 'style'] },
    { id: 'outer', label: '아우터', level: 'root', parent_id: null, sort_order: 30, attribute_schema: ['color', 'material', 'pattern', 'fit', 'season', 'gender', 'style'] },
    { id: 'dress', label: '원피스', level: 'root', parent_id: null, sort_order: 40, attribute_schema: ['color', 'material', 'pattern', 'fit', 'season', 'gender', 'style'] },
    { id: 'shoes', label: '신발', level: 'root', parent_id: null, sort_order: 50, attribute_schema: ['color', 'material', 'season', 'gender', 'style'] },
    { id: 'bag', label: '가방', level: 'root', parent_id: null, sort_order: 60, attribute_schema: ['color', 'material', 'pattern', 'season', 'gender', 'style'] },
    { id: 'accessory', label: '액세서리', level: 'root', parent_id: null, sort_order: 70, attribute_schema: ['color', 'material', 'season', 'gender', 'style'] },
    { id: 'top:blouse', label: '블라우스', level: 'subcategory', parent_id: 'top', parent_label: '상의', sort_order: 11 },
    { id: 'top:tshirt', label: '티셔츠', level: 'subcategory', parent_id: 'top', parent_label: '상의', sort_order: 12 },
    { id: 'top:knit', label: '니트', level: 'subcategory', parent_id: 'top', parent_label: '상의', sort_order: 13 },
    { id: 'top:shirt', label: '셔츠', level: 'subcategory', parent_id: 'top', parent_label: '상의', sort_order: 14 },
    { id: 'top:cardigan', label: '가디건', level: 'subcategory', parent_id: 'top', parent_label: '상의', sort_order: 15 },
    { id: 'bottom:pants', label: '팬츠', level: 'subcategory', parent_id: 'bottom', parent_label: '하의', sort_order: 21 },
    { id: 'bottom:denim', label: '데님', level: 'subcategory', parent_id: 'bottom', parent_label: '하의', sort_order: 22 },
    { id: 'bottom:skirt', label: '스커트', level: 'subcategory', parent_id: 'bottom', parent_label: '하의', sort_order: 23 },
    { id: 'bottom:shorts', label: '쇼츠', level: 'subcategory', parent_id: 'bottom', parent_label: '하의', sort_order: 24 },
    { id: 'bottom:leggings', label: '레깅스', level: 'subcategory', parent_id: 'bottom', parent_label: '하의', sort_order: 25 },
    { id: 'outer:trench-coat', label: '트렌치코트', level: 'subcategory', parent_id: 'outer', parent_label: '아우터', sort_order: 31 },
    { id: 'outer:jumper', label: '점퍼', level: 'subcategory', parent_id: 'outer', parent_label: '아우터', sort_order: 32 }
];

export const lookitAttributeValues: ProductAttributeValue[] = [
    { id: 'color:ivory', kind: 'color', label: '아이보리', aliases: ['ivory'] },
    { id: 'color:melange-gray', kind: 'color', label: '멜란지 그레이', aliases: ['melange gray', 'gray'] },
    { id: 'color:beige', kind: 'color', label: '베이지', aliases: ['beige'] },
    { id: 'color:charcoal', kind: 'color', label: '차콜', aliases: ['charcoal'] },
    { id: 'color:medium-blue', kind: 'color', label: '중청', aliases: ['mid blue', 'medium blue'] },
    { id: 'color:dark-blue', kind: 'color', label: '진청', aliases: ['dark blue'] },
    { id: 'color:oatmeal', kind: 'color', label: '오트밀', aliases: ['oatmeal'] },
    { id: 'color:navy', kind: 'color', label: '네이비', aliases: ['navy'] },
    { id: 'color:cream', kind: 'color', label: '크림', aliases: ['cream'] },
    { id: 'color:sky-blue', kind: 'color', label: '소라', aliases: ['sky blue'] },
    { id: 'color:pink', kind: 'color', label: '핑크', aliases: ['pink'] },
    { id: 'color:black', kind: 'color', label: '블랙', aliases: ['black'] },
    { id: 'material:cotton', kind: 'material', label: 'cotton', aliases: ['면'] },
    { id: 'material:nylon', kind: 'material', label: 'nylon', aliases: ['나일론'] },
    { id: 'material:polyester', kind: 'material', label: 'polyester', aliases: ['폴리에스터'] },
    { id: 'material:polyurethane', kind: 'material', label: 'polyurethane', aliases: ['스판'] },
    { id: 'material:wool', kind: 'material', label: 'wool', aliases: ['울'] },
    { id: 'material:acrylic', kind: 'material', label: 'acrylic', aliases: ['아크릴'] },
    { id: 'material:rayon', kind: 'material', label: 'rayon', aliases: ['레이온'] },
    { id: 'pattern:solid', kind: 'pattern', label: 'solid', aliases: ['무지'] },
    { id: 'pattern:denim', kind: 'pattern', label: 'denim', aliases: ['데님'] },
    { id: 'pattern:rib', kind: 'pattern', label: 'rib', aliases: ['골지'] },
    { id: 'pattern:lace', kind: 'pattern', label: 'lace', aliases: ['레이스'] },
    { id: 'pattern:cable', kind: 'pattern', label: 'cable', aliases: ['케이블'] },
    { id: 'fit:semi-crop', kind: 'fit', label: 'semi_crop', aliases: ['세미 크롭'] },
    { id: 'fit:relaxed', kind: 'fit', label: 'relaxed', aliases: ['루즈핏'] },
    { id: 'fit:straight', kind: 'fit', label: 'straight', aliases: ['스트레이트'] },
    { id: 'fit:standard', kind: 'fit', label: 'standard', aliases: ['스탠다드'] },
    { id: 'fit:regular', kind: 'fit', label: 'regular', aliases: ['레귤러'] },
    { id: 'fit:jogger', kind: 'fit', label: 'jogger', aliases: ['조거'] },
    { id: 'fit:wide', kind: 'fit', label: 'wide', aliases: ['와이드'] },
    { id: 'season:all', kind: 'season', label: '사계절', aliases: ['all season'] },
    { id: 'season:spring', kind: 'season', label: '봄', aliases: ['spring'] },
    { id: 'season:summer', kind: 'season', label: '여름', aliases: ['summer'] },
    { id: 'season:fall', kind: 'season', label: '가을', aliases: ['fall', 'autumn'] },
    { id: 'season:winter', kind: 'season', label: '겨울', aliases: ['winter'] },
    { id: 'gender:women', kind: 'gender', label: '여성', aliases: ['women'] },
    { id: 'gender:unisex', kind: 'gender', label: '유니섹스', aliases: ['unisex'] },
    { id: 'style:daily', kind: 'style', label: '데일리', aliases: ['daily'] },
    { id: 'style:minimal', kind: 'style', label: '미니멀', aliases: ['minimal'] },
    { id: 'style:feminine', kind: 'style', label: '페미닌', aliases: ['feminine'] },
    { id: 'style:casual', kind: 'style', label: '캐주얼', aliases: ['casual'] }
];

type ProductClusterSeed = Omit<ProductCluster, 'brand_id' | 'wholesale_tag_id' | 'category_root_id' | 'category_root' | 'subcategory_id' | 'subcategory' | 'category_path' | 'attribute_profile'> & {
    brand_id?: string | null;
    wholesale_tag_id?: string | null;
    season_ids?: string[];
    gender_ids?: string[];
    style_tag_ids?: string[];
};

type ListingOptionSeed = Omit<ListingOption, 'variant_id'> & {
    variant_id?: string | null;
};

type ListingSeed = Omit<ProductListing, 'benefits' | 'public_effective_price' | 'category_root_id' | 'category_root' | 'subcategory_id' | 'subcategory' | 'category_path' | 'external_product_id' | 'platform_listing_id' | 'seller_product_id' | 'variant_ids' | 'options' | 'source_status' | 'freshness'> & {
    external_product_id?: string | null;
    platform_listing_id?: string | null;
    seller_product_id?: string | null;
    options: ListingOptionSeed[];
    source_status?: SourceStatus;
    freshness?: Partial<ListingFreshness>;
    reference_benefits?: Benefit[];
};

type SnapshotSeed = {
    id: string;
    listing_id: string;
    product_cluster_id?: string;
    variant_id?: string | null;
    checked_at: string;
    collected_at?: string;
    listed_price: number;
    instant_discount: number;
    public_coupon_discount: number;
    shipping_fee: number;
    free_shipping_condition: string | null;
    availability_status: AvailabilityStatus;
    option_availability: OptionAvailability[];
    source_status?: SourceStatus;
};

function lookupKey(value: string): string {
    return value.trim().toLowerCase();
}

function fallbackId(prefix: string, value: string): string {
    return `${prefix}:${lookupKey(value).replace(/[\s_]+/g, '-')}`;
}

function categoryIdForLabel(label: string, parentId: string | null = null): string | null {
    const category = lookitCategories.find((item) => {
        if (lookupKey(item.label) !== lookupKey(label)) return false;
        if (parentId === null) return item.level === 'root';
        return item.parent_id === parentId;
    });

    return category?.id || null;
}

function attributeId(kind: ProductAttributeKind, value: string): string {
    const normalizedValue = lookupKey(value);
    const attribute = lookitAttributeValues.find((item) => (
        item.kind === kind &&
        (lookupKey(item.label) === normalizedValue || item.aliases.some((alias) => lookupKey(alias) === normalizedValue))
    ));

    return attribute?.id || fallbackId(kind, value);
}

function categoryParts(category: string): {
    category_root_id: string;
    category_root: ProductCategoryRoot;
    subcategory_id: string | null;
    subcategory: ProductSubcategory | null;
    category_path: ProductCategoryPath;
} {
    const [root, subcategory] = category.split('>').map((part) => part.trim()).filter(Boolean);
    const categoryRoot = (root || '기타') as ProductCategoryRoot;
    const categoryRootId = categoryIdForLabel(categoryRoot) || fallbackId('category', categoryRoot);
    const productSubcategory = subcategory ? subcategory as ProductSubcategory : null;
    const subcategoryId = productSubcategory ? categoryIdForLabel(productSubcategory, categoryRootId) || fallbackId(categoryRootId, productSubcategory) : null;
    const categoryPath: ProductCategoryPath = {
        root_id: categoryRootId,
        root_label: categoryRoot,
        subcategory_id: subcategoryId,
        subcategory_label: productSubcategory,
        path_label: productSubcategory ? `${categoryRoot} > ${productSubcategory}` : categoryRoot
    };

    return {
        category_root_id: categoryRootId,
        category_root: categoryRoot,
        subcategory_id: subcategoryId,
        subcategory: productSubcategory,
        category_path: categoryPath
    };
}

function brandIdForName(brandName?: string | null): string | null {
    if (!brandName) return null;
    return lookitBrands.find((brand) => lookupKey(brand.name) === lookupKey(brandName))?.id || fallbackId('brand', brandName);
}

function wholesaleTagIdForName(tagName?: string | null): string | null {
    if (!tagName) return null;
    return lookitWholesaleTags.find((tag) => (
        lookupKey(tag.name) === lookupKey(tagName) ||
        tag.aliases.some((alias) => lookupKey(alias) === lookupKey(tagName))
    ))?.id || fallbackId('wholesale-tag', tagName);
}

function attributeProfileForCluster(seed: ProductClusterSeed): ProductAttributeProfile {
    return {
        color_ids: seed.colors.map((color) => attributeId('color', color)),
        material_ids: seed.materials.map((material) => attributeId('material', material)),
        pattern_ids: seed.pattern ? [attributeId('pattern', seed.pattern)] : [],
        fit_ids: seed.fit ? [attributeId('fit', seed.fit)] : [],
        season_ids: seed.season_ids || ['season:all'],
        gender_ids: seed.gender_ids || ['gender:women'],
        style_tag_ids: seed.style_tag_ids || ['style:daily']
    };
}

function createProductCluster(seed: ProductClusterSeed): ProductCluster {
    const brand_id = seed.brand_id ?? brandIdForName(seed.brand_name);
    const wholesale_tag_id = seed.wholesale_tag_id ?? wholesaleTagIdForName(seed.wholesale_tag_name);

    return {
        ...seed,
        brand_id,
        wholesale_tag_id,
        ...categoryParts(seed.category),
        attribute_profile: attributeProfileForCluster(seed)
    };
}

function listingCategoryParts(productClusterId: string): {
    category_root_id: string | null;
    category_root: ProductCategoryRoot | null;
    subcategory_id: string | null;
    subcategory: ProductSubcategory | null;
    category_path: ProductCategoryPath | null;
} {
    const cluster = lookitProductClusters.find((item) => item.id === productClusterId) || null;

    return {
        category_root_id: cluster?.category_root_id || null,
        category_root: cluster?.category_root || null,
        subcategory_id: cluster?.subcategory_id || null,
        subcategory: cluster?.subcategory || null,
        category_path: cluster?.category_path || null
    };
}

function optionId(listingId: string, color: string, size: string): string {
    return `${listingId}::${color}::${size}`;
}

function variantId(productClusterId: string, color: string, size: string): string {
    return `${productClusterId}::${color}::${size}`;
}

function options(
    listingId: string,
    colors: string[],
    sizes: string[],
    availability_status: AvailabilityStatus = 'in_stock',
    stock_count?: number,
    restock_expected_at: string | null = null
): ListingOption[] {
    const resolvedStockCount = stock_count ?? (
        availability_status === 'sold_out'
            ? 0
            : availability_status === 'low_stock'
                ? 2
                : availability_status === 'checking'
                    ? undefined
                    : 8
    );

    return colors.flatMap((color) => sizes.map((size) => ({
        id: optionId(listingId, color, size),
        listing_id: listingId,
        variant_id: null,
        color,
        size,
        availability_status,
        ...(typeof resolvedStockCount === 'number' ? { stock_count: resolvedStockCount } : {}),
        restock_expected_at,
        restock_alert_available: availability_status === 'sold_out' || availability_status === 'restock_expected',
        last_checked_at: syncTime,
        checked_at: syncTime,
        collected_at: syncTime
    })));
}

function benefit(
    id: string,
    listing_id: string,
    benefit_type: Benefit['benefit_type'],
    name: string,
    amount: number | null,
    conditions: string,
    is_public_default: boolean
): Benefit {
    return {
        id,
        listing_id,
        benefit_type,
        name,
        amount,
        rate: null,
        conditions,
        is_public_default,
        starts_at: null,
        ends_at: null
    };
}

function defaultBenefits(listing: ListingSeed): Benefit[] {
    const items: Benefit[] = [
        benefit(`${listing.id}-instant`, listing.id, 'instant_discount', '즉시 할인', listing.instant_discount, '모든 사용자에게 즉시 적용', true),
        benefit(`${listing.id}-public-coupon`, listing.id, 'public_coupon', '공개 쿠폰', listing.public_coupon_discount, '상품 페이지에서 누구나 받을 수 있는 쿠폰', true),
        benefit(`${listing.id}-shipping`, listing.id, 'shipping', '단품 기준 기본 배송비', listing.shipping_fee, listing.free_shipping_condition || '기본 배송비 적용', true)
    ];

    return items.concat(listing.reference_benefits || []);
}

function createListing(seed: ListingSeed): Listing {
    const {
        reference_benefits,
        external_product_id,
        platform_listing_id,
        seller_product_id,
        options: seedOptions,
        source_status,
        freshness,
        ...listing
    } = seed;
    const cluster = lookitProductClusters.find((item) => item.id === listing.product_cluster_id) || null;
    const checkedAt = listing.checked_at || listing.last_checked_at;
    const collectedAt = listing.collected_at || checkedAt;
    const listingSourceStatus = source_status || 'mock';
    const listingConfidence = listing.confidence ?? cluster?.confidence_level ?? 0.7;
    const listingFreshness = normalizeListingFreshness({
        ...freshness,
        status: freshness?.status || 'stale',
        checkedAt: freshness?.checkedAt || freshness?.checked_at || checkedAt,
        collectedAt: freshness?.collectedAt || freshness?.collected_at || collectedAt,
        confidenceScore: freshness?.confidenceScore || freshness?.confidence_score || listingConfidence,
        sourceStatus: freshness?.sourceStatus || freshness?.source_status || listingSourceStatus
    }, new Date(syncTime));
    const listingOptions: ListingOption[] = seedOptions.map((option) => {
        const optionCheckedAt = option.checked_at || option.last_checked_at || checkedAt;
        const optionCollectedAt = option.collected_at || collectedAt;
        const optionFreshness = option.freshness || normalizeListingFreshness({
            ...listingFreshness,
            checkedAt: optionCheckedAt,
            collectedAt: optionCollectedAt,
            sourceStatus: listingFreshness.sourceStatus,
            confidenceScore: listingFreshness.confidenceScore
        }, new Date(syncTime));

        return {
            ...option,
            variant_id: option.variant_id ?? variantId(listing.product_cluster_id, option.color, option.size),
            last_checked_at: option.last_checked_at || optionCheckedAt,
            checked_at: optionCheckedAt,
            collected_at: optionCollectedAt,
            restock_alert_available: option.restock_alert_available ?? (option.availability_status === 'sold_out' || option.availability_status === 'restock_expected'),
            freshness: optionFreshness
        };
    });

    return {
        ...listing,
        external_product_id: external_product_id || listing.id,
        platform_listing_id: platform_listing_id || listing.id,
        seller_product_id: seller_product_id || listing.id,
        ...listingCategoryParts(listing.product_cluster_id),
        public_effective_price: calculatePublicEffectivePrice(listing),
        variant_ids: Array.from(new Set(listingOptions.map((option) => option.variant_id).filter(Boolean))) as string[],
        options: listingOptions,
        benefits: defaultBenefits(seed),
        checked_at: checkedAt,
        collected_at: collectedAt,
        confidence: listingConfidence,
        freshness: listingFreshness,
        source_status: listingSourceStatus
    };
}

function minutesBefore(value: string, minutes: number): string {
    const timestamp = new Date(value).getTime();
    if (!Number.isFinite(timestamp)) return value;
    return new Date(timestamp - (minutes * 60 * 1000)).toISOString();
}

function createSnapshot(seed: SnapshotSeed): PriceSnapshot {
    const listing = lookitListings.find((item) => item.id === seed.listing_id) || null;
    const firstOption = seed.option_availability[0] || null;
    const productClusterId = seed.product_cluster_id || listing?.product_cluster_id || '';
    const variant_id = seed.variant_id ?? (listing && firstOption ? variantId(listing.product_cluster_id, firstOption.color, firstOption.size) : null);
    const collected_at = seed.collected_at || minutesBefore(seed.checked_at, 2);
    const source_status = seed.source_status || listing?.source_status || 'mock';
    const public_effective_price = calculatePublicEffectivePrice(seed);

    return {
        ...seed,
        product_cluster_id: productClusterId,
        variant_id,
        collected_at,
        source_status,
        public_effective_price,
        listingId: seed.listing_id,
        productClusterId,
        variantId: variant_id,
        checkedAt: seed.checked_at,
        collectedAt: collected_at,
        effectivePrice: public_effective_price,
        basePrice: seed.listed_price,
        shippingFee: seed.shipping_fee,
        couponApplied: seed.public_coupon_discount > 0,
        stockStatus: seed.availability_status,
        sourceStatus: source_status,
        instantDiscount: seed.instant_discount,
        publicCoupon: seed.public_coupon_discount,
        freeShippingCondition: seed.free_shipping_condition,
        optionAvailability: seed.option_availability,
        freshness: listing?.freshness,
        listing_freshness: listing?.freshness
    };
}

export const lookitPlatforms: Platform[] = [
    {
        id: 'platform-musinsa',
        name: '무신사',
        type: 'marketplace',
        api_available: false,
        affiliate_available: true,
        terms_status: 'partner_required',
        last_sync_at: syncTime
    },
    {
        id: 'platform-29cm',
        name: '29CM',
        type: 'marketplace',
        api_available: false,
        affiliate_available: true,
        terms_status: 'partner_required',
        last_sync_at: syncTime
    },
    {
        id: 'platform-wconcept',
        name: 'W컨셉',
        type: 'marketplace',
        api_available: false,
        affiliate_available: true,
        terms_status: 'partner_required',
        last_sync_at: syncTime
    },
    {
        id: 'platform-hago',
        name: 'HAGO',
        type: 'marketplace',
        api_available: false,
        affiliate_available: true,
        terms_status: 'checking',
        last_sync_at: syncTime
    },
    {
        id: 'platform-zigzag',
        name: '지그재그',
        type: 'marketplace',
        api_available: false,
        affiliate_available: true,
        terms_status: 'partner_required',
        last_sync_at: syncTime
    },
    {
        id: 'platform-ably',
        name: '에이블리',
        type: 'marketplace',
        api_available: false,
        affiliate_available: true,
        terms_status: 'partner_required',
        last_sync_at: syncTime
    },
    {
        id: 'platform-naver-smartstore',
        name: '네이버 스마트스토어',
        type: 'search_portal',
        api_available: false,
        affiliate_available: true,
        terms_status: 'partner_required',
        last_sync_at: syncTime
    },
    {
        id: 'platform-spa-official',
        name: 'SPA 공식몰',
        type: 'brand_mall',
        api_available: false,
        affiliate_available: false,
        terms_status: 'checking',
        last_sync_at: syncTime
    },
    {
        id: 'platform-personal-mall',
        name: '개인 쇼핑몰',
        type: 'personal_mall',
        api_available: false,
        affiliate_available: false,
        terms_status: 'checking',
        last_sync_at: syncTime
    }
];

export const lookitSellers: Seller[] = [
    {
        id: 'seller-muse-brand',
        name: 'MUSE Brand',
        official_site_url: 'https://example.com/muse-brand',
        platform_ids: ['platform-musinsa', 'platform-29cm', 'platform-wconcept', 'platform-hago'],
        seller_type: 'brand',
        brand_ids: ['brand-muse'],
        wholesale_tag_ids: [],
        platform_accounts: [
            { platform_id: 'platform-musinsa', external_seller_id: 'muse-brand-musinsa', shop_url: 'https://example.com/musinsa/muse-brand' },
            { platform_id: 'platform-29cm', external_seller_id: 'muse-brand-29cm', shop_url: 'https://example.com/29cm/muse-brand' },
            { platform_id: 'platform-wconcept', external_seller_id: 'muse-brand-wconcept', shop_url: 'https://example.com/wconcept/muse-brand' },
            { platform_id: 'platform-hago', external_seller_id: 'muse-brand-hago', shop_url: 'https://example.com/hago/muse-brand' }
        ]
    },
    {
        id: 'seller-noir-label',
        name: 'NOIR Label',
        official_site_url: 'https://example.com/noir-label',
        platform_ids: ['platform-musinsa', 'platform-29cm', 'platform-wconcept', 'platform-hago'],
        seller_type: 'brand',
        brand_ids: ['brand-noir-label'],
        wholesale_tag_ids: [],
        platform_accounts: [
            { platform_id: 'platform-musinsa', external_seller_id: 'noir-label-musinsa', shop_url: 'https://example.com/musinsa/noir-label' },
            { platform_id: 'platform-29cm', external_seller_id: 'noir-label-29cm', shop_url: 'https://example.com/29cm/noir-label' },
            { platform_id: 'platform-wconcept', external_seller_id: 'noir-label-wconcept', shop_url: 'https://example.com/wconcept/noir-label' },
            { platform_id: 'platform-hago', external_seller_id: 'noir-label-hago', shop_url: 'https://example.com/hago/noir-label' }
        ]
    },
    {
        id: 'seller-b-shop',
        name: 'B 쇼핑몰',
        official_site_url: 'https://example.com/b-shop',
        platform_ids: ['platform-personal-mall', 'platform-zigzag', 'platform-ably', 'platform-naver-smartstore'],
        seller_type: 'wholesale_retailer',
        brand_ids: [],
        wholesale_tag_ids: ['wholesale-forest', 'wholesale-liette', 'wholesale-vegetable'],
        platform_accounts: [
            { platform_id: 'platform-personal-mall', external_seller_id: 'b-shop-official', shop_url: 'https://example.com/b-shop' },
            { platform_id: 'platform-zigzag', external_seller_id: 'b-shop-zigzag', shop_url: 'https://example.com/zigzag/b-shop' },
            { platform_id: 'platform-ably', external_seller_id: 'b-shop-ably', shop_url: 'https://example.com/ably/b-shop' },
            { platform_id: 'platform-naver-smartstore', external_seller_id: 'b-shop-smartstore', shop_url: 'https://example.com/naver/b-shop' }
        ]
    },
    {
        id: 'seller-daily-mono',
        name: '데일리모노',
        official_site_url: 'https://example.com/daily-mono',
        platform_ids: ['platform-personal-mall', 'platform-zigzag', 'platform-naver-smartstore'],
        seller_type: 'personal_mall',
        brand_ids: [],
        wholesale_tag_ids: ['wholesale-forest', 'wholesale-liette'],
        platform_accounts: [
            { platform_id: 'platform-personal-mall', external_seller_id: 'daily-mono-official', shop_url: 'https://example.com/daily-mono' },
            { platform_id: 'platform-zigzag', external_seller_id: 'daily-mono-zigzag', shop_url: 'https://example.com/zigzag/daily-mono' },
            { platform_id: 'platform-naver-smartstore', external_seller_id: 'daily-mono-smartstore', shop_url: 'https://example.com/naver/daily-mono' }
        ]
    },
    {
        id: 'seller-atelier-select',
        name: '아틀리에 셀렉트',
        official_site_url: 'https://example.com/atelier-select',
        platform_ids: ['platform-29cm', 'platform-wconcept', 'platform-personal-mall'],
        seller_type: 'select_shop',
        brand_ids: ['brand-urban-thread', 'brand-atelier-mood'],
        wholesale_tag_ids: [],
        platform_accounts: [
            { platform_id: 'platform-29cm', external_seller_id: 'atelier-select-29cm', shop_url: 'https://example.com/29cm/atelier-select' },
            { platform_id: 'platform-wconcept', external_seller_id: 'atelier-select-wconcept', shop_url: 'https://example.com/wconcept/atelier-select' },
            { platform_id: 'platform-personal-mall', external_seller_id: 'atelier-select-official', shop_url: 'https://example.com/atelier-select' }
        ]
    },
    {
        id: 'seller-spa-official',
        name: 'SPA 공식몰',
        official_site_url: 'https://example.com/spa-official',
        platform_ids: ['platform-spa-official'],
        seller_type: 'brand',
        brand_ids: ['brand-urban-thread'],
        wholesale_tag_ids: [],
        platform_accounts: [
            { platform_id: 'platform-spa-official', external_seller_id: 'spa-official', shop_url: 'https://example.com/spa-official' }
        ]
    }
];

export const lookitBrands: Brand[] = [
    {
        id: 'brand-muse',
        name: 'MUSE Brand',
        normalized_name: 'muse brand',
        brand_type: 'domestic_brand',
        official_site_url: 'https://example.com/muse-brand',
        seller_ids: ['seller-muse-brand'],
        platform_ids: ['platform-musinsa', 'platform-29cm', 'platform-wconcept', 'platform-hago'],
        source_status: 'mock',
        created_at: '2026-06-24T08:00:00+09:00',
        updated_at: syncTime
    },
    {
        id: 'brand-noir-label',
        name: 'NOIR Label',
        normalized_name: 'noir label',
        brand_type: 'domestic_brand',
        official_site_url: 'https://example.com/noir-label',
        seller_ids: ['seller-noir-label'],
        platform_ids: ['platform-musinsa', 'platform-29cm', 'platform-wconcept', 'platform-hago'],
        source_status: 'mock',
        created_at: '2026-06-24T08:00:00+09:00',
        updated_at: syncTime
    },
    {
        id: 'brand-urban-thread',
        name: 'Urban Thread',
        normalized_name: 'urban thread',
        brand_type: 'marketplace_brand',
        official_site_url: null,
        seller_ids: ['seller-atelier-select', 'seller-spa-official'],
        platform_ids: ['platform-29cm', 'platform-naver-smartstore', 'platform-spa-official'],
        source_status: 'mock',
        created_at: '2026-06-24T08:00:00+09:00',
        updated_at: syncTime
    },
    {
        id: 'brand-atelier-mood',
        name: 'Atelier Mood',
        normalized_name: 'atelier mood',
        brand_type: 'marketplace_brand',
        official_site_url: null,
        seller_ids: ['seller-atelier-select'],
        platform_ids: ['platform-wconcept'],
        source_status: 'mock',
        created_at: '2026-06-24T08:00:00+09:00',
        updated_at: syncTime
    }
];

export const lookitWholesaleTags: WholesaleTag[] = [
    {
        id: 'wholesale-forest',
        name: 'FOREST',
        normalized_name: 'forest',
        tag_type: 'dongdaemun_tag',
        aliases: ['포레스트'],
        seller_ids: ['seller-b-shop', 'seller-daily-mono'],
        platform_ids: ['platform-personal-mall', 'platform-zigzag', 'platform-naver-smartstore'],
        source_status: 'mock',
        created_at: '2026-06-24T08:00:00+09:00',
        updated_at: syncTime
    },
    {
        id: 'wholesale-liette',
        name: 'LIETTE',
        normalized_name: 'liette',
        tag_type: 'dongdaemun_tag',
        aliases: ['리에뜨'],
        seller_ids: ['seller-b-shop', 'seller-daily-mono'],
        platform_ids: ['platform-personal-mall', 'platform-zigzag', 'platform-ably', 'platform-naver-smartstore'],
        source_status: 'mock',
        created_at: '2026-06-24T08:00:00+09:00',
        updated_at: syncTime
    },
    {
        id: 'wholesale-vegetable',
        name: 'VEGETABLE',
        normalized_name: 'vegetable',
        tag_type: 'dongdaemun_tag',
        aliases: ['베지터블'],
        seller_ids: ['seller-b-shop'],
        platform_ids: ['platform-zigzag', 'platform-ably'],
        source_status: 'mock',
        created_at: '2026-06-24T08:00:00+09:00',
        updated_at: syncTime
    }
];

export const lookitProductClusters: ProductCluster[] = [
    createProductCluster({
        id: 'cluster-muse-crop-cardigan',
        representative_image_url: `${imageBase}/photo-1515886657613-9f3515b0c78f?auto=format&fit=crop&w=900&q=80`,
        canonical_name: '라운드넥 세미 크롭 니트 가디건',
        brand_name: 'MUSE Brand',
        wholesale_tag_name: null,
        category: '상의 > 가디건',
        colors: ['아이보리', '멜란지 그레이'],
        materials: ['cotton', 'nylon'],
        pattern: 'solid',
        fit: 'semi_crop',
        confidence_level: 0.94,
        cluster_type: 'brand_exact',
        source_status: 'mock',
        created_at: '2026-06-24T08:10:00+09:00',
        updated_at: syncTime
    }),
    createProductCluster({
        id: 'cluster-noir-trench',
        representative_image_url: `${imageBase}/photo-1485968579580-b6d095142e6e?auto=format&fit=crop&w=900&q=80`,
        canonical_name: '코튼 블렌드 하프 트렌치 코트',
        brand_name: 'NOIR Label',
        wholesale_tag_name: null,
        category: '아우터 > 트렌치코트',
        colors: ['베이지', '차콜'],
        materials: ['cotton', 'polyester'],
        pattern: 'solid',
        fit: 'relaxed',
        confidence_level: 0.91,
        cluster_type: 'brand_exact',
        source_status: 'mock',
        created_at: '2026-06-24T08:14:00+09:00',
        updated_at: syncTime
    }),
    createProductCluster({
        id: 'cluster-muse-straight-denim',
        representative_image_url: `${imageBase}/photo-1541099649105-f69ad21f3246?auto=format&fit=crop&w=900&q=80`,
        canonical_name: '미드블루 스트레이트 데님 팬츠',
        brand_name: 'MUSE Brand',
        wholesale_tag_name: null,
        category: '하의 > 데님',
        colors: ['중청', '진청'],
        materials: ['cotton', 'polyurethane'],
        pattern: 'denim',
        fit: 'straight',
        confidence_level: 0.9,
        cluster_type: 'brand_exact',
        source_status: 'mock',
        created_at: '2026-06-24T08:18:00+09:00',
        updated_at: syncTime
    }),
    createProductCluster({
        id: 'cluster-forest-zipup',
        representative_image_url: `${imageBase}/photo-1543076447-215ad9ba6923?auto=format&fit=crop&w=900&q=80`,
        canonical_name: '울 블렌드 투웨이 집업 니트',
        brand_name: null,
        wholesale_tag_name: 'FOREST',
        category: '상의 > 니트',
        colors: ['오트밀', '네이비'],
        materials: ['wool', 'acrylic'],
        pattern: 'rib',
        fit: 'standard',
        confidence_level: 0.84,
        cluster_type: 'wholesale_same',
        source_status: 'mock',
        created_at: '2026-06-24T08:20:00+09:00',
        updated_at: syncTime
    }),
    createProductCluster({
        id: 'cluster-liette-lace-blouse',
        representative_image_url: `${imageBase}/photo-1529139574466-a303027c1d8b?auto=format&fit=crop&w=900&q=80`,
        canonical_name: '리에뜨 레이스 핀턱 블라우스',
        brand_name: null,
        wholesale_tag_name: 'LIETTE',
        category: '상의 > 블라우스',
        colors: ['크림', '소라'],
        materials: ['rayon', 'polyester'],
        pattern: 'lace',
        fit: 'regular',
        confidence_level: 0.81,
        cluster_type: 'wholesale_same',
        source_status: 'mock',
        created_at: '2026-06-24T08:24:00+09:00',
        updated_at: syncTime
    }),
    createProductCluster({
        id: 'cluster-vegetable-cargo-pants',
        representative_image_url: `${imageBase}/photo-1594633312681-425c7b97ccd1?auto=format&fit=crop&w=900&q=80`,
        canonical_name: '베지터블 코튼 카고 팬츠',
        brand_name: null,
        wholesale_tag_name: 'VEGETABLE',
        category: '하의 > 팬츠',
        colors: ['핑크', '블랙'],
        materials: ['cotton', 'nylon'],
        pattern: 'solid',
        fit: 'jogger',
        confidence_level: 0.82,
        cluster_type: 'wholesale_same',
        source_status: 'mock',
        created_at: '2026-06-24T08:26:00+09:00',
        updated_at: syncTime
    }),
    createProductCluster({
        id: 'cluster-similar-cable-cardigan',
        representative_image_url: `${imageBase}/photo-1551489186-cf8726f514f8?auto=format&fit=crop&w=900&q=80`,
        canonical_name: '케이블 라운드 니트 가디건',
        brand_name: 'Urban Thread',
        wholesale_tag_name: null,
        category: '상의 > 가디건',
        colors: ['아이보리'],
        materials: ['acrylic', 'wool'],
        pattern: 'cable',
        fit: 'semi_crop',
        confidence_level: 0.68,
        cluster_type: 'similar_style',
        source_status: 'mock',
        created_at: '2026-06-24T08:28:00+09:00',
        updated_at: syncTime
    }),
    createProductCluster({
        id: 'cluster-similar-soft-blouson',
        representative_image_url: `${imageBase}/photo-1496747611176-843222e1e57c?auto=format&fit=crop&w=900&q=80`,
        canonical_name: '소프트 라운드 블루종 재킷',
        brand_name: 'Atelier Mood',
        wholesale_tag_name: null,
        category: '아우터 > 점퍼',
        colors: ['크림', '블랙'],
        materials: ['polyester', 'rayon'],
        pattern: 'solid',
        fit: 'relaxed',
        confidence_level: 0.63,
        cluster_type: 'similar_style',
        source_status: 'mock',
        created_at: '2026-06-24T08:31:00+09:00',
        updated_at: syncTime
    }),
    createProductCluster({
        id: 'cluster-similar-pintuck-blouse',
        representative_image_url: `${imageBase}/photo-1515372039744-b8f02a3ae446?auto=format&fit=crop&w=900&q=80`,
        canonical_name: '핀턱 레이스 카라 블라우스',
        brand_name: null,
        wholesale_tag_name: 'VEGETABLE 추정',
        category: '상의 > 블라우스',
        colors: ['아이보리', '핑크'],
        materials: ['cotton', 'polyester'],
        pattern: 'lace',
        fit: 'regular',
        confidence_level: 0.66,
        cluster_type: 'similar_style',
        source_status: 'mock',
        created_at: '2026-06-24T08:34:00+09:00',
        updated_at: syncTime
    }),
    createProductCluster({
        id: 'cluster-similar-wide-slacks',
        representative_image_url: `${imageBase}/photo-1506629905607-d405b7a30db9?auto=format&fit=crop&w=900&q=80`,
        canonical_name: '핀턱 와이드 슬랙스',
        brand_name: 'Urban Thread',
        wholesale_tag_name: null,
        category: '하의 > 팬츠',
        colors: ['차콜', '블랙'],
        materials: ['polyester', 'rayon'],
        pattern: 'solid',
        fit: 'wide',
        confidence_level: 0.65,
        cluster_type: 'similar_style',
        source_status: 'mock',
        created_at: '2026-06-24T08:36:00+09:00',
        updated_at: syncTime
    })
];

export const lookitListings: Listing[] = [
    createListing({
        id: 'listing-muse-cardigan-musinsa',
        product_cluster_id: 'cluster-muse-crop-cardigan',
        platform_id: 'platform-musinsa',
        seller_id: 'seller-muse-brand',
        title: 'MUSE 라운드넥 세미 크롭 니트 가디건',
        description: '브랜드 공식 입점 상품. 공개 쿠폰과 무신사 기본 무료배송 기준.',
        product_url: 'https://example.com/musinsa/muse-cardigan',
        image_urls: [`${imageBase}/photo-1515886657613-9f3515b0c78f?auto=format&fit=crop&w=900&q=80`],
        listed_price: 42900,
        sale_price: 39900,
        instant_discount: 3000,
        public_coupon_discount: 3000,
        base_shipping_fee: 0,
        shipping_fee: 0,
        free_shipping_condition: '무신사 기본 무료배송',
        currency: 'KRW',
        availability_status: 'in_stock',
        options: options('listing-muse-cardigan-musinsa', ['아이보리', '멜란지 그레이'], ['S', 'M'], 'in_stock', 12),
        last_checked_at: '2026-06-26T08:58:00+09:00',
        collected_at: '2026-06-26T08:54:00+09:00',
        freshness: {
            status: 'fresh',
            refreshInterval: 180,
            nextRefreshAt: '2026-06-26T11:58:00+09:00',
            confidence: 'high',
            confidenceScore: 0.92
        },
        reference_benefits: [
            benefit('benefit-muse-cardigan-musinsa-membership', 'listing-muse-cardigan-musinsa', 'membership', '회원 등급 추가 적립', null, '개인 등급별 상이', false)
        ]
    }),
    createListing({
        id: 'listing-muse-cardigan-29cm',
        product_cluster_id: 'cluster-muse-crop-cardigan',
        platform_id: 'platform-29cm',
        seller_id: 'seller-muse-brand',
        title: 'Round Neck Semi Crop Knit Cardigan',
        description: '29CM 브랜드관 입점 상품. 공개 장바구니 쿠폰 적용 가능.',
        product_url: 'https://example.com/29cm/muse-cardigan',
        image_urls: [`${imageBase}/photo-1515886657613-9f3515b0c78f?auto=format&fit=crop&w=900&q=80`],
        listed_price: 44900,
        sale_price: 42900,
        instant_discount: 2000,
        public_coupon_discount: 4000,
        base_shipping_fee: 0,
        shipping_fee: 0,
        free_shipping_condition: '3만원 이상 무료배송',
        currency: 'KRW',
        availability_status: 'in_stock',
        options: options('listing-muse-cardigan-29cm', ['아이보리'], ['S', 'M'], 'in_stock', 7).concat(options('listing-muse-cardigan-29cm', ['멜란지 그레이'], ['S'], 'in_stock', 4), options('listing-muse-cardigan-29cm', ['멜란지 그레이'], ['M'], 'low_stock', 1)),
        last_checked_at: '2026-06-26T03:45:00+09:00',
        collected_at: '2026-06-26T03:40:00+09:00',
        freshness: {
            status: 'stale',
            refreshInterval: 180,
            nextRefreshAt: '2026-06-26T06:45:00+09:00',
            confidence: 'medium',
            confidenceScore: 0.72
        }
    }),
    createListing({
        id: 'listing-muse-cardigan-wconcept',
        product_cluster_id: 'cluster-muse-crop-cardigan',
        platform_id: 'platform-wconcept',
        seller_id: 'seller-muse-brand',
        title: 'MUSE 세미 크롭 니트 가디건',
        description: 'W컨셉 입점 동일 상품. 공개 할인가와 다운로드 쿠폰 반영.',
        product_url: 'https://example.com/wconcept/muse-cardigan',
        image_urls: [`${imageBase}/photo-1515886657613-9f3515b0c78f?auto=format&fit=crop&w=900&q=80`],
        listed_price: 45900,
        sale_price: 40900,
        instant_discount: 5000,
        public_coupon_discount: 2000,
        base_shipping_fee: 0,
        shipping_fee: 0,
        free_shipping_condition: '무료배송',
        currency: 'KRW',
        availability_status: 'low_stock',
        options: options('listing-muse-cardigan-wconcept', ['아이보리'], ['S', 'M'], 'low_stock', 2).concat(options('listing-muse-cardigan-wconcept', ['멜란지 그레이'], ['S', 'M'], 'sold_out', 0, '2026-07-02T09:00:00+09:00')),
        last_checked_at: '2026-06-24T08:35:00+09:00',
        freshness: {
            status: 'expired',
            refreshInterval: 180,
            nextRefreshAt: '2026-06-24T11:35:00+09:00',
            confidence: 'low',
            confidenceScore: 0.42
        }
    }),
    createListing({
        id: 'listing-muse-cardigan-hago',
        product_cluster_id: 'cluster-muse-crop-cardigan',
        platform_id: 'platform-hago',
        seller_id: 'seller-muse-brand',
        title: 'MUSE Brand 니트 가디건 아이보리',
        description: 'HAGO 입점 동일 상품. 현재 목업은 재고 API 실패로 마지막 확인 기준 품절 처리.',
        product_url: 'https://example.com/hago/muse-cardigan',
        image_urls: [`${imageBase}/photo-1515886657613-9f3515b0c78f?auto=format&fit=crop&w=900&q=80`],
        listed_price: 41900,
        sale_price: 40900,
        instant_discount: 1000,
        public_coupon_discount: 2000,
        base_shipping_fee: 3000,
        shipping_fee: 3000,
        free_shipping_condition: '5만원 이상 무료배송',
        currency: 'KRW',
        availability_status: 'sold_out',
        options: options('listing-muse-cardigan-hago', ['아이보리'], ['S', 'M'], 'sold_out', 0, '2026-07-06T09:00:00+09:00'),
        last_checked_at: '2026-06-26T08:25:00+09:00',
        collected_at: '2026-06-26T08:20:00+09:00',
        source_status: 'needs_verification',
        freshness: {
            status: 'failed',
            refreshInterval: 60,
            nextRefreshAt: '2026-06-26T09:25:00+09:00',
            failureReason: 'HAGO 재고 API 응답 실패',
            confidence: 'low',
            confidenceScore: 0.24,
            sourceStatus: 'needs_verification'
        }
    }),
    createListing({
        id: 'listing-noir-trench-musinsa',
        product_cluster_id: 'cluster-noir-trench',
        platform_id: 'platform-musinsa',
        seller_id: 'seller-noir-label',
        title: 'NOIR 코튼 블렌드 하프 트렌치',
        description: '브랜드 공식 입점 상품.',
        product_url: 'https://example.com/musinsa/noir-trench',
        image_urls: [`${imageBase}/photo-1485968579580-b6d095142e6e?auto=format&fit=crop&w=900&q=80`],
        listed_price: 139000,
        sale_price: 124000,
        instant_discount: 15000,
        public_coupon_discount: 5000,
        base_shipping_fee: 0,
        shipping_fee: 0,
        free_shipping_condition: '무료배송',
        currency: 'KRW',
        availability_status: 'in_stock',
        options: options('listing-noir-trench-musinsa', ['베이지', '차콜'], ['S', 'M', 'L'], 'in_stock', 9),
        last_checked_at: '2026-06-24T08:40:00+09:00'
    }),
    createListing({
        id: 'listing-noir-trench-29cm',
        product_cluster_id: 'cluster-noir-trench',
        platform_id: 'platform-29cm',
        seller_id: 'seller-noir-label',
        title: 'Cotton Blend Half Trench Coat',
        description: '29CM 입점 동일 브랜드 상품.',
        product_url: 'https://example.com/29cm/noir-trench',
        image_urls: [`${imageBase}/photo-1485968579580-b6d095142e6e?auto=format&fit=crop&w=900&q=80`],
        listed_price: 142000,
        sale_price: 130000,
        instant_discount: 12000,
        public_coupon_discount: 7000,
        base_shipping_fee: 0,
        shipping_fee: 0,
        free_shipping_condition: '무료배송',
        currency: 'KRW',
        availability_status: 'in_stock',
        options: options('listing-noir-trench-29cm', ['베이지', '차콜'], ['S', 'M'], 'in_stock', 6),
        last_checked_at: '2026-06-24T08:32:00+09:00'
    }),
    createListing({
        id: 'listing-noir-trench-wconcept',
        product_cluster_id: 'cluster-noir-trench',
        platform_id: 'platform-wconcept',
        seller_id: 'seller-noir-label',
        title: 'NOIR Label 하프 트렌치 코트',
        description: 'W컨셉 입점 상품. 쿠폰 조건 확인 가능.',
        product_url: 'https://example.com/wconcept/noir-trench',
        image_urls: [`${imageBase}/photo-1485968579580-b6d095142e6e?auto=format&fit=crop&w=900&q=80`],
        listed_price: 136000,
        sale_price: 128000,
        instant_discount: 8000,
        public_coupon_discount: 0,
        base_shipping_fee: 3000,
        shipping_fee: 3000,
        free_shipping_condition: '15만원 이상 무료배송',
        currency: 'KRW',
        availability_status: 'low_stock',
        options: options('listing-noir-trench-wconcept', ['베이지'], ['M'], 'low_stock', 1).concat(options('listing-noir-trench-wconcept', ['베이지'], ['S'], 'sold_out', 0, '2026-07-05T09:00:00+09:00'), options('listing-noir-trench-wconcept', ['차콜'], ['S', 'M'], 'in_stock', 5)),
        last_checked_at: '2026-06-24T08:22:00+09:00',
        reference_benefits: [
            benefit('benefit-noir-trench-wconcept-card', 'listing-noir-trench-wconcept', 'card', '카드 청구 할인', null, '카드사와 결제수단별 상이', false)
        ]
    }),
    createListing({
        id: 'listing-noir-trench-hago',
        product_cluster_id: 'cluster-noir-trench',
        platform_id: 'platform-hago',
        seller_id: 'seller-noir-label',
        title: '하프 트렌치 코트 베이지',
        description: 'HAGO 브랜드 편집전 동일 상품.',
        product_url: 'https://example.com/hago/noir-trench',
        image_urls: [`${imageBase}/photo-1485968579580-b6d095142e6e?auto=format&fit=crop&w=900&q=80`],
        listed_price: 135000,
        sale_price: 125000,
        instant_discount: 10000,
        public_coupon_discount: 5000,
        base_shipping_fee: 0,
        shipping_fee: 0,
        free_shipping_condition: '무료배송',
        currency: 'KRW',
        availability_status: 'in_stock',
        options: options('listing-noir-trench-hago', ['베이지'], ['S', 'M', 'L'], 'in_stock', 4),
        last_checked_at: '2026-06-24T08:20:00+09:00'
    }),
    createListing({
        id: 'listing-muse-denim-musinsa',
        product_cluster_id: 'cluster-muse-straight-denim',
        platform_id: 'platform-musinsa',
        seller_id: 'seller-muse-brand',
        title: 'MUSE 미드블루 스트레이트 데님 팬츠',
        description: '브랜드 공식 입점 하의 상품. 공개 쿠폰과 기본 무료배송 기준.',
        product_url: 'https://example.com/musinsa/muse-straight-denim',
        image_urls: [`${imageBase}/photo-1541099649105-f69ad21f3246?auto=format&fit=crop&w=900&q=80`],
        listed_price: 69900,
        sale_price: 64900,
        instant_discount: 5000,
        public_coupon_discount: 4000,
        base_shipping_fee: 0,
        shipping_fee: 0,
        free_shipping_condition: '무신사 기본 무료배송',
        currency: 'KRW',
        availability_status: 'in_stock',
        options: options('listing-muse-denim-musinsa', ['중청', '진청'], ['S', 'M', 'L'], 'in_stock', 6),
        last_checked_at: '2026-06-24T08:52:00+09:00'
    }),
    createListing({
        id: 'listing-muse-denim-29cm',
        product_cluster_id: 'cluster-muse-straight-denim',
        platform_id: 'platform-29cm',
        seller_id: 'seller-muse-brand',
        title: 'Mid Blue Straight Denim Pants',
        description: '29CM 브랜드관 입점 동일 하의 상품. 공개 장바구니 쿠폰 반영.',
        product_url: 'https://example.com/29cm/muse-straight-denim',
        image_urls: [`${imageBase}/photo-1541099649105-f69ad21f3246?auto=format&fit=crop&w=900&q=80`],
        listed_price: 71900,
        sale_price: 67900,
        instant_discount: 4000,
        public_coupon_discount: 5000,
        base_shipping_fee: 0,
        shipping_fee: 0,
        free_shipping_condition: '3만원 이상 무료배송',
        currency: 'KRW',
        availability_status: 'low_stock',
        options: options('listing-muse-denim-29cm', ['중청'], ['S', 'M'], 'low_stock', 2).concat(options('listing-muse-denim-29cm', ['진청'], ['M', 'L'], 'in_stock', 5)),
        last_checked_at: '2026-06-24T08:43:00+09:00',
        reference_benefits: [
            benefit('benefit-muse-denim-29cm-membership', 'listing-muse-denim-29cm', 'membership', '회원 등급 적립', null, '개인 등급별 상이', false)
        ]
    }),
    createListing({
        id: 'listing-forest-zipup-personal',
        product_cluster_id: 'cluster-forest-zipup',
        platform_id: 'platform-personal-mall',
        seller_id: 'seller-b-shop',
        title: '울 투웨이 니트 집업 오트밀',
        description: 'B 쇼핑몰 자사몰 상품. 도매택 FOREST 동일 공급 상품 추정.',
        product_url: 'https://example.com/b-shop/forest-zipup',
        image_urls: [`${imageBase}/photo-1543076447-215ad9ba6923?auto=format&fit=crop&w=900&q=80`],
        listed_price: 39800,
        sale_price: 39800,
        instant_discount: 0,
        public_coupon_discount: 0,
        base_shipping_fee: 3000,
        shipping_fee: 3000,
        free_shipping_condition: '7만원 이상 무료배송',
        currency: 'KRW',
        availability_status: 'in_stock',
        options: options('listing-forest-zipup-personal', ['오트밀', '네이비'], ['Free'], 'in_stock', 10),
        last_checked_at: '2026-06-24T08:50:00+09:00'
    }),
    createListing({
        id: 'listing-forest-zipup-zigzag',
        product_cluster_id: 'cluster-forest-zipup',
        platform_id: 'platform-zigzag',
        seller_id: 'seller-b-shop',
        title: '[B쇼핑몰] 포레스트 투웨이 집업 니트',
        description: '같은 판매자의 지그재그 입점 상품. 공개 쿠폰과 플랫폼 무료배송 반영.',
        product_url: 'https://example.com/zigzag/b-shop-forest-zipup',
        image_urls: [`${imageBase}/photo-1543076447-215ad9ba6923?auto=format&fit=crop&w=900&q=80`],
        listed_price: 42900,
        sale_price: 39900,
        instant_discount: 3000,
        public_coupon_discount: 4000,
        base_shipping_fee: 3000,
        shipping_fee: 0,
        free_shipping_condition: '지그재그 행사 무료배송',
        currency: 'KRW',
        availability_status: 'in_stock',
        options: options('listing-forest-zipup-zigzag', ['오트밀'], ['Free'], 'in_stock', 8).concat(options('listing-forest-zipup-zigzag', ['네이비'], ['Free'], 'sold_out', 0, '2026-06-30T09:00:00+09:00')),
        last_checked_at: '2026-06-24T08:47:00+09:00',
        reference_benefits: [
            benefit('benefit-forest-zipup-zigzag-new-user', 'listing-forest-zipup-zigzag', 'new_user', '신규회원 쿠폰', 3000, '신규 가입 사용자 한정', false)
        ]
    }),
    createListing({
        id: 'listing-forest-zipup-ably',
        product_cluster_id: 'cluster-forest-zipup',
        platform_id: 'platform-ably',
        seller_id: 'seller-b-shop',
        title: '포레스트 니트 집업 투웨이',
        description: '같은 판매자의 에이블리 입점 상품. 상품명과 모델컷은 다르지만 동일 공급 추정.',
        product_url: 'https://example.com/ably/b-shop-forest-zipup',
        image_urls: [`${imageBase}/photo-1543076447-215ad9ba6923?auto=format&fit=crop&w=900&q=80`],
        listed_price: 39900,
        sale_price: 38900,
        instant_discount: 1000,
        public_coupon_discount: 2500,
        base_shipping_fee: 3000,
        shipping_fee: 3000,
        free_shipping_condition: '5만원 이상 무료배송',
        currency: 'KRW',
        availability_status: 'low_stock',
        options: options('listing-forest-zipup-ably', ['오트밀'], ['Free'], 'low_stock', 1).concat(options('listing-forest-zipup-ably', ['네이비'], ['Free'], 'in_stock', 5)),
        last_checked_at: '2026-06-24T08:42:00+09:00'
    }),
    createListing({
        id: 'listing-forest-zipup-naver',
        product_cluster_id: 'cluster-forest-zipup',
        platform_id: 'platform-naver-smartstore',
        seller_id: 'seller-b-shop',
        title: 'B샵 울 블렌드 투웨이 집업 니트',
        description: '네이버 스마트스토어 상품. 스토어찜 쿠폰은 개인 조건으로 참고만 표시.',
        product_url: 'https://example.com/naver/b-shop-forest-zipup',
        image_urls: [`${imageBase}/photo-1543076447-215ad9ba6923?auto=format&fit=crop&w=900&q=80`],
        listed_price: 38500,
        sale_price: 38500,
        instant_discount: 0,
        public_coupon_discount: 1000,
        base_shipping_fee: 3000,
        shipping_fee: 3000,
        free_shipping_condition: '6만원 이상 무료배송',
        currency: 'KRW',
        availability_status: 'in_stock',
        options: options('listing-forest-zipup-naver', ['오트밀', '네이비'], ['Free'], 'in_stock', 7),
        last_checked_at: '2026-06-24T08:38:00+09:00',
        reference_benefits: [
            benefit('benefit-forest-zipup-naver-point', 'listing-forest-zipup-naver', 'point', '네이버페이 적립', null, '개인 결제수단과 멤버십별 상이', false)
        ]
    }),
    createListing({
        id: 'listing-forest-zipup-daily-mono',
        product_cluster_id: 'cluster-forest-zipup',
        platform_id: 'platform-personal-mall',
        seller_id: 'seller-daily-mono',
        title: '오트 투웨이 니트 집업',
        description: '다른 개인 쇼핑몰의 동일 공급 상품 추정.',
        product_url: 'https://example.com/daily-mono/forest-zipup',
        image_urls: [`${imageBase}/photo-1543076447-215ad9ba6923?auto=format&fit=crop&w=900&q=80`],
        listed_price: 41200,
        sale_price: 39200,
        instant_discount: 2000,
        public_coupon_discount: 0,
        base_shipping_fee: 3000,
        shipping_fee: 3000,
        free_shipping_condition: '8만원 이상 무료배송',
        currency: 'KRW',
        availability_status: 'in_stock',
        options: options('listing-forest-zipup-daily-mono', ['오트밀'], ['Free'], 'in_stock', 4),
        last_checked_at: '2026-06-24T08:30:00+09:00'
    }),
    createListing({
        id: 'listing-liette-blouse-personal',
        product_cluster_id: 'cluster-liette-lace-blouse',
        platform_id: 'platform-personal-mall',
        seller_id: 'seller-daily-mono',
        title: '레이스 핀턱 블라우스 크림',
        description: '자사몰 보세 상품. 도매택 LIETTE 동일 공급 추정.',
        product_url: 'https://example.com/daily-mono/liette-blouse',
        image_urls: [`${imageBase}/photo-1529139574466-a303027c1d8b?auto=format&fit=crop&w=900&q=80`],
        listed_price: 32800,
        sale_price: 32800,
        instant_discount: 0,
        public_coupon_discount: 0,
        base_shipping_fee: 3000,
        shipping_fee: 3000,
        free_shipping_condition: '6만원 이상 무료배송',
        currency: 'KRW',
        availability_status: 'in_stock',
        options: options('listing-liette-blouse-personal', ['크림', '소라'], ['Free'], 'in_stock', 6),
        last_checked_at: '2026-06-24T08:26:00+09:00'
    }),
    createListing({
        id: 'listing-liette-blouse-zigzag',
        product_cluster_id: 'cluster-liette-lace-blouse',
        platform_id: 'platform-zigzag',
        seller_id: 'seller-daily-mono',
        title: '[데일리모노] 리에뜨 레이스 블라우스',
        description: '지그재그 입점 상품. 플랫폼 공개 쿠폰 적용.',
        product_url: 'https://example.com/zigzag/daily-mono-liette-blouse',
        image_urls: [`${imageBase}/photo-1529139574466-a303027c1d8b?auto=format&fit=crop&w=900&q=80`],
        listed_price: 35900,
        sale_price: 34900,
        instant_discount: 1000,
        public_coupon_discount: 3000,
        base_shipping_fee: 3000,
        shipping_fee: 0,
        free_shipping_condition: '지그재그 행사 무료배송',
        currency: 'KRW',
        availability_status: 'in_stock',
        options: options('listing-liette-blouse-zigzag', ['크림'], ['Free'], 'in_stock', 8).concat(options('listing-liette-blouse-zigzag', ['소라'], ['Free'], 'low_stock', 2)),
        last_checked_at: '2026-06-24T08:24:00+09:00'
    }),
    createListing({
        id: 'listing-liette-blouse-ably',
        product_cluster_id: 'cluster-liette-lace-blouse',
        platform_id: 'platform-ably',
        seller_id: 'seller-b-shop',
        title: '핀턱 레이스 블라우스',
        description: '에이블리 입점 상품. 동일 공급 상품 추정.',
        product_url: 'https://example.com/ably/b-shop-liette-blouse',
        image_urls: [`${imageBase}/photo-1529139574466-a303027c1d8b?auto=format&fit=crop&w=900&q=80`],
        listed_price: 34500,
        sale_price: 33000,
        instant_discount: 1500,
        public_coupon_discount: 2000,
        base_shipping_fee: 2500,
        shipping_fee: 2500,
        free_shipping_condition: '5만원 이상 무료배송',
        currency: 'KRW',
        availability_status: 'low_stock',
        options: options('listing-liette-blouse-ably', ['크림'], ['Free'], 'low_stock', 1).concat(options('listing-liette-blouse-ably', ['소라'], ['Free'], 'sold_out', 0, '2026-07-01T09:00:00+09:00')),
        last_checked_at: '2026-06-24T08:18:00+09:00'
    }),
    createListing({
        id: 'listing-liette-blouse-naver',
        product_cluster_id: 'cluster-liette-lace-blouse',
        platform_id: 'platform-naver-smartstore',
        seller_id: 'seller-daily-mono',
        title: '리에뜨 핀턱 레이스 블라우스',
        description: '네이버 스마트스토어 상품. 공개 쿠폰만 기본 가격에 반영.',
        product_url: 'https://example.com/naver/daily-mono-liette-blouse',
        image_urls: [`${imageBase}/photo-1529139574466-a303027c1d8b?auto=format&fit=crop&w=900&q=80`],
        listed_price: 33800,
        sale_price: 33800,
        instant_discount: 0,
        public_coupon_discount: 1000,
        base_shipping_fee: 3000,
        shipping_fee: 3000,
        free_shipping_condition: '6만원 이상 무료배송',
        currency: 'KRW',
        availability_status: 'in_stock',
        options: options('listing-liette-blouse-naver', ['크림', '소라'], ['Free'], 'in_stock', 5),
        last_checked_at: '2026-06-24T08:12:00+09:00'
    }),
    createListing({
        id: 'listing-vegetable-pants-zigzag',
        product_cluster_id: 'cluster-vegetable-cargo-pants',
        platform_id: 'platform-zigzag',
        seller_id: 'seller-b-shop',
        title: '[B쇼핑몰] 베지터블 카고 팬츠',
        description: '같은 도매택 하의 팬츠 상품 추정. 지그재그 공개 쿠폰과 행사 무료배송 반영.',
        product_url: 'https://example.com/zigzag/b-shop-vegetable-cargo-pants',
        image_urls: [`${imageBase}/photo-1594633312681-425c7b97ccd1?auto=format&fit=crop&w=900&q=80`],
        listed_price: 45900,
        sale_price: 43900,
        instant_discount: 2000,
        public_coupon_discount: 3000,
        base_shipping_fee: 3000,
        shipping_fee: 0,
        free_shipping_condition: '지그재그 행사 무료배송',
        currency: 'KRW',
        availability_status: 'in_stock',
        options: options('listing-vegetable-pants-zigzag', ['핑크', '블랙'], ['S', 'M'], 'in_stock', 5),
        last_checked_at: '2026-06-24T08:36:00+09:00'
    }),
    createListing({
        id: 'listing-vegetable-pants-ably',
        product_cluster_id: 'cluster-vegetable-cargo-pants',
        platform_id: 'platform-ably',
        seller_id: 'seller-b-shop',
        title: 'VEGETABLE 코튼 카고 팬츠',
        description: '에이블리 입점 동일 공급 팬츠 상품 추정. 가격/재고 API 연결 대기 상태.',
        product_url: 'https://example.com/ably/b-shop-vegetable-pants',
        image_urls: [`${imageBase}/photo-1594633312681-425c7b97ccd1?auto=format&fit=crop&w=900&q=80`],
        listed_price: 44900,
        sale_price: 42900,
        instant_discount: 2000,
        public_coupon_discount: 1500,
        base_shipping_fee: 2500,
        shipping_fee: 2500,
        free_shipping_condition: '5만원 이상 무료배송',
        currency: 'KRW',
        availability_status: 'checking',
        options: options('listing-vegetable-pants-ably', ['핑크', '블랙'], ['S', 'M'], 'checking'),
        last_checked_at: '2026-06-24T08:28:00+09:00',
        source_status: 'api_pending',
        freshness: {
            status: 'api_pending',
            refreshInterval: 60,
            nextRefreshAt: null,
            failureReason: '에이블리 가격/재고 API 연결 대기',
            confidence: 'unknown',
            confidenceScore: 0,
            sourceStatus: 'api_pending'
        }
    }),
    createListing({
        id: 'listing-similar-cable-cardigan-29cm',
        product_cluster_id: 'cluster-similar-cable-cardigan',
        platform_id: 'platform-29cm',
        seller_id: 'seller-atelier-select',
        title: '케이블 라운드 니트 가디건',
        description: '유사 디자인 참고 상품.',
        product_url: 'https://example.com/29cm/similar-cable-cardigan',
        image_urls: [`${imageBase}/photo-1551489186-cf8726f514f8?auto=format&fit=crop&w=900&q=80`],
        listed_price: 52900,
        sale_price: 49900,
        instant_discount: 3000,
        public_coupon_discount: 3000,
        base_shipping_fee: 0,
        shipping_fee: 0,
        free_shipping_condition: '무료배송',
        currency: 'KRW',
        availability_status: 'in_stock',
        options: options('listing-similar-cable-cardigan-29cm', ['아이보리'], ['Free'], 'in_stock', 4),
        last_checked_at: '2026-06-24T07:58:00+09:00'
    }),
    createListing({
        id: 'listing-similar-soft-blouson-wconcept',
        product_cluster_id: 'cluster-similar-soft-blouson',
        platform_id: 'platform-wconcept',
        seller_id: 'seller-atelier-select',
        title: '소프트 라운드 블루종 재킷',
        description: '핏과 소재가 비슷한 유사 디자인 상품.',
        product_url: 'https://example.com/wconcept/similar-soft-blouson',
        image_urls: [`${imageBase}/photo-1496747611176-843222e1e57c?auto=format&fit=crop&w=900&q=80`],
        listed_price: 79900,
        sale_price: 71900,
        instant_discount: 8000,
        public_coupon_discount: 5000,
        base_shipping_fee: 0,
        shipping_fee: 0,
        free_shipping_condition: '무료배송',
        currency: 'KRW',
        availability_status: 'in_stock',
        options: options('listing-similar-soft-blouson-wconcept', ['크림', '블랙'], ['S', 'M'], 'in_stock', 5),
        last_checked_at: '2026-06-24T07:50:00+09:00'
    }),
    createListing({
        id: 'listing-similar-pintuck-blouse-personal',
        product_cluster_id: 'cluster-similar-pintuck-blouse',
        platform_id: 'platform-personal-mall',
        seller_id: 'seller-daily-mono',
        title: '핀턱 레이스 카라 블라우스',
        description: '도매택이 다르거나 미확인인 유사 디자인 상품.',
        product_url: 'https://example.com/daily-mono/similar-pintuck-blouse',
        image_urls: [`${imageBase}/photo-1515372039744-b8f02a3ae446?auto=format&fit=crop&w=900&q=80`],
        listed_price: 29900,
        sale_price: 29900,
        instant_discount: 0,
        public_coupon_discount: 0,
        base_shipping_fee: 3000,
        shipping_fee: 3000,
        free_shipping_condition: '6만원 이상 무료배송',
        currency: 'KRW',
        availability_status: 'in_stock',
        options: options('listing-similar-pintuck-blouse-personal', ['아이보리'], ['Free'], 'in_stock', 3).concat(options('listing-similar-pintuck-blouse-personal', ['핑크'], ['Free'], 'sold_out', 0, '2026-07-04T09:00:00+09:00')),
        last_checked_at: '2026-06-24T07:45:00+09:00'
    }),
    createListing({
        id: 'listing-similar-wide-slacks-naver',
        product_cluster_id: 'cluster-similar-wide-slacks',
        platform_id: 'platform-naver-smartstore',
        seller_id: 'seller-daily-mono',
        title: '핀턱 와이드 슬랙스 차콜',
        description: '하의 유사 디자인 참고 상품. 공개 쿠폰만 기본 가격에 반영.',
        product_url: 'https://example.com/naver/daily-mono-wide-slacks',
        image_urls: [`${imageBase}/photo-1506629905607-d405b7a30db9?auto=format&fit=crop&w=900&q=80`],
        listed_price: 39800,
        sale_price: 37800,
        instant_discount: 2000,
        public_coupon_discount: 1000,
        base_shipping_fee: 3000,
        shipping_fee: 3000,
        free_shipping_condition: '6만원 이상 무료배송',
        currency: 'KRW',
        availability_status: 'in_stock',
        options: options('listing-similar-wide-slacks-naver', ['차콜', '블랙'], ['S', 'M', 'L'], 'in_stock', 5),
        last_checked_at: '2026-06-24T07:42:00+09:00'
    })
];

function createVariantsFromListings(): Variant[] {
    const variantMap = new Map<string, Variant>();

    lookitListings.forEach((listing) => {
        const cluster = lookitProductClusters.find((item) => item.id === listing.product_cluster_id) || null;

        listing.options.forEach((option) => {
            if (!option.variant_id) return;

            const current = variantMap.get(option.variant_id);
            if (current) {
                if (!current.source_listing_option_ids.includes(option.id)) {
                    current.source_listing_option_ids.push(option.id);
                }
                return;
            }

            variantMap.set(option.variant_id, {
                id: option.variant_id,
                product_cluster_id: listing.product_cluster_id,
                color: option.color,
                color_id: attributeId('color', option.color),
                size: option.size,
                material_ids: cluster?.attribute_profile.material_ids || [],
                option_signature: `${option.color} / ${option.size}`,
                source_listing_option_ids: [option.id],
                created_at: syncTime,
                updated_at: syncTime
            });
        });
    });

    return Array.from(variantMap.values());
}

export const lookitVariants: Variant[] = createVariantsFromListings();

export const lookitPriceSnapshots: PriceSnapshot[] = [
    createSnapshot({
        id: 'snapshot-muse-cardigan-20260328',
        listing_id: 'listing-muse-cardigan-musinsa',
        checked_at: '2026-03-28T09:00:00+09:00',
        listed_price: 48900,
        instant_discount: 0,
        public_coupon_discount: 2000,
        shipping_fee: 0,
        free_shipping_condition: '무신사 기본 무료배송',
        availability_status: 'in_stock',
        option_availability: [{ color: '아이보리', size: 'S', availability_status: 'in_stock' }]
    }),
    createSnapshot({
        id: 'snapshot-muse-cardigan-20260405',
        listing_id: 'listing-muse-cardigan-musinsa',
        checked_at: '2026-04-05T09:00:00+09:00',
        listed_price: 48900,
        instant_discount: 3000,
        public_coupon_discount: 2000,
        shipping_fee: 0,
        free_shipping_condition: '무신사 기본 무료배송',
        availability_status: 'in_stock',
        option_availability: [{ color: '아이보리', size: 'S', availability_status: 'in_stock' }]
    }),
    createSnapshot({
        id: 'snapshot-muse-cardigan-20260413',
        listing_id: 'listing-muse-cardigan-musinsa',
        checked_at: '2026-04-13T09:00:00+09:00',
        listed_price: 46900,
        instant_discount: 2000,
        public_coupon_discount: 2000,
        shipping_fee: 0,
        free_shipping_condition: '무신사 기본 무료배송',
        availability_status: 'in_stock',
        option_availability: [{ color: '아이보리', size: 'S', availability_status: 'in_stock' }]
    }),
    createSnapshot({
        id: 'snapshot-muse-cardigan-20260421',
        listing_id: 'listing-muse-cardigan-musinsa',
        checked_at: '2026-04-21T09:00:00+09:00',
        listed_price: 45900,
        instant_discount: 3000,
        public_coupon_discount: 3000,
        shipping_fee: 0,
        free_shipping_condition: '무신사 기본 무료배송',
        availability_status: 'in_stock',
        option_availability: [{ color: '아이보리', size: 'S', availability_status: 'in_stock' }]
    }),
    createSnapshot({
        id: 'snapshot-muse-cardigan-20260429',
        listing_id: 'listing-muse-cardigan-musinsa',
        checked_at: '2026-04-29T09:00:00+09:00',
        listed_price: 45900,
        instant_discount: 1000,
        public_coupon_discount: 2000,
        shipping_fee: 0,
        free_shipping_condition: '무신사 기본 무료배송',
        availability_status: 'in_stock',
        option_availability: [{ color: '아이보리', size: 'S', availability_status: 'low_stock' }]
    }),
    createSnapshot({
        id: 'snapshot-muse-cardigan-20260507',
        listing_id: 'listing-muse-cardigan-musinsa',
        checked_at: '2026-05-07T09:00:00+09:00',
        listed_price: 44900,
        instant_discount: 3000,
        public_coupon_discount: 2000,
        shipping_fee: 0,
        free_shipping_condition: '무신사 기본 무료배송',
        availability_status: 'in_stock',
        option_availability: [{ color: '아이보리', size: 'S', availability_status: 'in_stock' }]
    }),
    createSnapshot({
        id: 'snapshot-muse-cardigan-20260515',
        listing_id: 'listing-muse-cardigan-musinsa',
        checked_at: '2026-05-15T09:00:00+09:00',
        listed_price: 44900,
        instant_discount: 2000,
        public_coupon_discount: 3000,
        shipping_fee: 0,
        free_shipping_condition: '무신사 기본 무료배송',
        availability_status: 'in_stock',
        option_availability: [{ color: '아이보리', size: 'S', availability_status: 'in_stock' }]
    }),
    createSnapshot({
        id: 'snapshot-muse-cardigan-20260523',
        listing_id: 'listing-muse-cardigan-musinsa',
        checked_at: '2026-05-23T09:00:00+09:00',
        listed_price: 43900,
        instant_discount: 2000,
        public_coupon_discount: 3000,
        shipping_fee: 0,
        free_shipping_condition: '무신사 기본 무료배송',
        availability_status: 'in_stock',
        option_availability: [{ color: '아이보리', size: 'S', availability_status: 'in_stock' }]
    }),
    createSnapshot({
        id: 'snapshot-muse-cardigan-20260531',
        listing_id: 'listing-muse-cardigan-musinsa',
        checked_at: '2026-05-31T09:00:00+09:00',
        listed_price: 43900,
        instant_discount: 3000,
        public_coupon_discount: 3000,
        shipping_fee: 0,
        free_shipping_condition: '무신사 기본 무료배송',
        availability_status: 'in_stock',
        option_availability: [{ color: '아이보리', size: 'S', availability_status: 'in_stock' }]
    }),
    createSnapshot({
        id: 'snapshot-muse-cardigan-20260608',
        listing_id: 'listing-muse-cardigan-musinsa',
        checked_at: '2026-06-08T09:00:00+09:00',
        listed_price: 42900,
        instant_discount: 2000,
        public_coupon_discount: 3000,
        shipping_fee: 0,
        free_shipping_condition: '무신사 기본 무료배송',
        availability_status: 'in_stock',
        option_availability: [{ color: '아이보리', size: 'S', availability_status: 'in_stock' }]
    }),
    createSnapshot({
        id: 'snapshot-muse-cardigan-20260616',
        listing_id: 'listing-muse-cardigan-musinsa',
        checked_at: '2026-06-16T09:00:00+09:00',
        listed_price: 42900,
        instant_discount: 3000,
        public_coupon_discount: 3000,
        shipping_fee: 0,
        free_shipping_condition: '무신사 기본 무료배송',
        availability_status: 'low_stock',
        option_availability: [{ color: '아이보리', size: 'S', availability_status: 'low_stock' }]
    }),
    createSnapshot({
        id: 'snapshot-muse-cardigan-20260624',
        listing_id: 'listing-muse-cardigan-musinsa',
        checked_at: '2026-06-24T09:00:00+09:00',
        listed_price: 42900,
        instant_discount: 3000,
        public_coupon_discount: 3000,
        shipping_fee: 0,
        free_shipping_condition: '무신사 기본 무료배송',
        availability_status: 'in_stock',
        option_availability: [{ color: '아이보리', size: 'S', availability_status: 'in_stock' }]
    }),
    createSnapshot({
        id: 'snapshot-muse-denim-20260524',
        listing_id: 'listing-muse-denim-musinsa',
        checked_at: '2026-05-24T09:00:00+09:00',
        listed_price: 74900,
        instant_discount: 3000,
        public_coupon_discount: 3000,
        shipping_fee: 0,
        free_shipping_condition: '무신사 기본 무료배송',
        availability_status: 'in_stock',
        option_availability: [{ color: '중청', size: 'M', availability_status: 'in_stock' }]
    }),
    createSnapshot({
        id: 'snapshot-muse-denim-20260608',
        listing_id: 'listing-muse-denim-musinsa',
        checked_at: '2026-06-08T09:00:00+09:00',
        listed_price: 72900,
        instant_discount: 4000,
        public_coupon_discount: 3000,
        shipping_fee: 0,
        free_shipping_condition: '무신사 기본 무료배송',
        availability_status: 'low_stock',
        option_availability: [{ color: '중청', size: 'M', availability_status: 'low_stock' }]
    }),
    createSnapshot({
        id: 'snapshot-muse-denim-20260624',
        listing_id: 'listing-muse-denim-musinsa',
        checked_at: '2026-06-24T09:00:00+09:00',
        listed_price: 69900,
        instant_discount: 5000,
        public_coupon_discount: 4000,
        shipping_fee: 0,
        free_shipping_condition: '무신사 기본 무료배송',
        availability_status: 'in_stock',
        option_availability: [{ color: '중청', size: 'M', availability_status: 'in_stock' }]
    })
];

export const lookitUserSavedItems: UserSavedItem[] = [
    {
        id: 'saved-user-demo-liette-blouse-top',
        user_id: 'user-demo',
        product_cluster_id: 'cluster-liette-lace-blouse',
        listing_id: null,
        listing_option_id: null,
        target_color: '크림',
        target_size: 'Free',
        selected_category_id: 'top',
        selected_category_label: '상의',
        selected_item_label: '상의 기본 검색 영역',
        selected_region_label: '상의 기본 검색 영역',
        selected_region: { ...topDefaultRegion },
        source_request_id: 'image-search-demo-20260624-001',
        saved_price: 31900,
        created_at: '2026-06-16T10:15:00+09:00'
    },
    {
        id: 'saved-user-demo-muse-denim-bottom',
        user_id: 'user-demo',
        product_cluster_id: 'cluster-muse-straight-denim',
        listing_id: 'listing-muse-denim-musinsa',
        listing_option_id: null,
        target_color: '중청',
        target_size: 'M',
        selected_category_id: 'bottom',
        selected_category_label: '하의',
        selected_item_label: '하의 기본 검색 영역',
        selected_region_label: '하의 기본 검색 영역',
        selected_region: { ...bottomDefaultRegion },
        source_request_id: 'image-search-demo-20260624-001',
        saved_price: 60900,
        created_at: '2026-06-20T21:40:00+09:00'
    },
    {
        id: 'saved-user-demo-liette-restock-sora',
        user_id: 'user-demo',
        product_cluster_id: 'cluster-liette-lace-blouse',
        listing_id: 'listing-liette-blouse-ably',
        listing_option_id: optionId('listing-liette-blouse-ably', '소라', 'Free'),
        target_color: '소라',
        target_size: 'Free',
        selected_category_id: 'top',
        selected_category_label: '상의',
        selected_item_label: '사용자 지정 영역',
        selected_region_label: '사용자 지정 영역',
        selected_region: { x: 20, y: 12, width: 58, height: 36 },
        source_request_id: 'image-search-demo-20260624-001',
        saved_price: 0,
        created_at: '2026-06-22T11:00:00+09:00'
    }
];

export const lookitPriceAlerts: PriceAlert[] = [
    {
        id: 'alert-user-demo-liette-blouse-lowest-90d',
        user_id: 'user-demo',
        product_cluster_id: 'cluster-liette-lace-blouse',
        listing_id: null,
        listing_option_id: null,
        alert_scope: 'product_cluster',
        alert_type: 'lowest_90d',
        saved_item_id: 'saved-user-demo-liette-blouse-top',
        selected_category_id: 'top',
        selected_category_label: '상의',
        selected_region_label: '상의 기본 검색 영역',
        selected_region: { ...topDefaultRegion },
        source_request_id: 'image-search-demo-20260624-001',
        baseline_product_name: '리에뜨 레이스 핀턱 블라우스',
        baseline_image_url: `${imageBase}/photo-1529139574466-a303027c1d8b?auto=format&fit=crop&w=900&q=80`,
        baseline_category_label: '상의',
        baseline_region_label: '상의 기본 검색 영역',
        target_color: '크림',
        target_size: 'Free',
        baseline_price: 31900,
        threshold_price: null,
        threshold_percent: null,
        channels: ['in_app'],
        enabled: true,
        last_sent_at: '2026-06-16T09:10:00+09:00',
        created_at: '2026-06-16T10:20:00+09:00',
        updated_at: '2026-06-16T10:20:00+09:00'
    },
    {
        id: 'alert-user-demo-muse-denim-target',
        user_id: 'user-demo',
        product_cluster_id: 'cluster-muse-straight-denim',
        listing_id: 'listing-muse-denim-musinsa',
        listing_option_id: null,
        alert_scope: 'listing',
        alert_type: 'target_price',
        saved_item_id: 'saved-user-demo-muse-denim-bottom',
        selected_category_id: 'bottom',
        selected_category_label: '하의',
        selected_region_label: '하의 기본 검색 영역',
        selected_region: { ...bottomDefaultRegion },
        source_request_id: 'image-search-demo-20260624-001',
        baseline_product_name: '미드블루 스트레이트 데님 팬츠',
        baseline_image_url: `${imageBase}/photo-1541099649105-f69ad21f3246?auto=format&fit=crop&w=900&q=80`,
        baseline_category_label: '하의',
        baseline_region_label: '하의 기본 검색 영역',
        target_color: '중청',
        target_size: 'M',
        baseline_price: 60900,
        threshold_price: 56000,
        threshold_percent: null,
        channels: ['in_app', 'browser_push'],
        enabled: true,
        last_sent_at: null,
        created_at: '2026-06-20T21:45:00+09:00',
        updated_at: '2026-06-20T21:45:00+09:00'
    },
    {
        id: 'alert-user-demo-liette-restock',
        user_id: 'user-demo',
        product_cluster_id: 'cluster-liette-lace-blouse',
        listing_id: 'listing-liette-blouse-ably',
        listing_option_id: optionId('listing-liette-blouse-ably', '소라', 'Free'),
        alert_scope: 'listing_option',
        alert_type: 'restock',
        saved_item_id: 'saved-user-demo-liette-restock-sora',
        selected_category_id: 'top',
        selected_category_label: '상의',
        selected_region_label: '사용자 지정 영역',
        selected_region: { x: 20, y: 12, width: 58, height: 36 },
        source_request_id: 'image-search-demo-20260624-001',
        baseline_product_name: '리에뜨 레이스 핀턱 블라우스',
        baseline_image_url: `${imageBase}/photo-1529139574466-a303027c1d8b?auto=format&fit=crop&w=900&q=80`,
        baseline_category_label: '상의',
        baseline_region_label: '사용자 지정 영역',
        target_color: '소라',
        target_size: 'Free',
        baseline_price: null,
        threshold_price: null,
        threshold_percent: null,
        channels: ['in_app'],
        enabled: true,
        last_sent_at: null,
        created_at: '2026-06-22T11:00:00+09:00',
        updated_at: '2026-06-22T11:00:00+09:00'
    }
];

export const lookitImageSearchRequests: ImageSearchRequest[] = [
    {
        id: 'image-search-demo-20260624-001',
        user_id: 'user-demo',
        upload_image_id: 'upload-demo-cardigan-screenshot',
        local_mock_image_id: null,
        image_url: 'https://example.com/uploads/demo-cardigan-screenshot.jpg',
        cropped_region: { ...topDefaultRegion },
        detected_items: [
            {
                id: 'mock-detected-top-blouse',
                item_index: 0,
                category: 'top',
                label: '상의 > 블라우스',
                confidence: 0.86,
                boundingBox: { ...topDefaultRegion },
                maskUrl: null,
                maskPolygon: null,
                source: 'mock',
                needsUserConfirmation: false,
                confirmationReason: null,
                colors: ['크림'],
                cropped_region: { ...topDefaultRegion }
            },
            {
                id: 'mock-detected-bottom-pants',
                item_index: 1,
                category: 'bottom',
                label: '하의 > 데님',
                confidence: 0.79,
                boundingBox: { ...bottomDefaultRegion },
                maskUrl: null,
                maskPolygon: null,
                source: 'mock',
                needsUserConfirmation: false,
                confirmationReason: null,
                colors: ['중청'],
                cropped_region: { ...bottomDefaultRegion }
            },
            {
                id: 'mock-detected-shoes',
                item_index: 2,
                category: 'shoes',
                label: '신발',
                confidence: 0.52,
                boundingBox: { ...shoesDefaultRegion },
                maskUrl: null,
                maskPolygon: null,
                source: 'mock',
                needsUserConfirmation: true,
                confirmationReason: 'low_confidence',
                colors: ['블랙'],
                cropped_region: { ...shoesDefaultRegion }
            }
        ],
        selected_item_index: 0,
        selected_candidate_id: 'mock-detected-top-blouse',
        selected_category_id: 'top',
        search_targets: [
            {
                id: 'target-top',
                label: '상의 타깃',
                selectedCategory: 'top',
                selectedRegion: { ...topDefaultRegion },
                regionSource: 'category_default',
                adjustmentStatus: 'defaulted',
                selectedCandidateId: 'mock-detected-top-blouse',
                selectedItemIndex: 0,
                updatedAt: '2026-06-24T08:05:00+09:00'
            },
            {
                id: 'target-bottom',
                label: '하의 타깃',
                selectedCategory: 'bottom',
                selectedRegion: { ...bottomDefaultRegion },
                regionSource: 'category_default',
                adjustmentStatus: 'defaulted',
                selectedCandidateId: 'mock-detected-bottom-pants',
                selectedItemIndex: 1,
                updatedAt: '2026-06-24T08:05:00+09:00'
            },
            {
                id: 'target-shoes',
                label: '신발 타깃',
                selectedCategory: 'shoes',
                selectedRegion: { ...shoesDefaultRegion },
                regionSource: 'category_default',
                adjustmentStatus: 'defaulted',
                selectedCandidateId: 'mock-detected-shoes',
                selectedItemIndex: 2,
                updatedAt: '2026-06-24T08:05:00+09:00'
            }
        ],
        active_search_target_id: 'target-top',
        search_status: 'completed',
        created_at: '2026-06-24T08:05:00+09:00',
        expires_at: '2026-07-24T08:05:00+09:00'
    }
];

export const lookitMockPricingMismatches = {
    listings: validateListingEffectivePrices(lookitListings),
    snapshots: validatePriceSnapshotEffectivePrices(lookitPriceSnapshots)
};

export const lookitMockData = {
    categories: lookitCategories,
    attributeValues: lookitAttributeValues,
    platforms: lookitPlatforms,
    sellers: lookitSellers,
    brands: lookitBrands,
    wholesaleTags: lookitWholesaleTags,
    productClusters: lookitProductClusters,
    variants: lookitVariants,
    listings: lookitListings,
    priceSnapshots: lookitPriceSnapshots,
    userSavedItems: lookitUserSavedItems,
    priceAlerts: lookitPriceAlerts,
    imageSearchRequests: lookitImageSearchRequests
};
